/**
 * Password re-authentication dialog used before revealing masked payloads.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

export interface ReauthDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with a short-lived step-up token after successful password verification. */
  onSuccess: (stepUpToken: string) => void;
  title?: string;
  description?: string;
}

const ReauthDialog: React.FC<ReauthDialogProps> = ({
  open,
  onClose,
  onSuccess,
  title = 'Confirm your password',
  description = 'Re-enter your account password to view this transmitted payload.',
}) => {
  const { token } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setPassword('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiService.reauthenticate(password, token || undefined);
      if (!res.success || !res.data?.step_up_token) {
        setError(res.error || res.message || 'Re-authentication failed');
        return;
      }
      const stepUp = res.data.step_up_token;
      setPassword('');
      onSuccess(stepUp);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Re-authentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Verifying…' : 'Confirm'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReauthDialog;
